import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Gift, Loader2, CheckCircle2, AlertCircle, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useValidateGift, useRedeemGift } from "@/hooks/use-gifts";
import type { RedeemGiftResult } from "@/hooks/use-gifts";

export default function RedeemGift() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const [code, setCode] = useState("");
  const [submittedCode, setSubmittedCode] = useState<string | undefined>(undefined);
  const [redeemed, setRedeemed] = useState<RedeemGiftResult | null>(null);

  // Auto-fill (and auto-validate) the code when arriving via the gift SMS link,
  // e.g. https://timecleankw.com/redeem?code=TC-GIFT-XXXXXX
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const codeFromUrl = params.get("code");
    if (codeFromUrl) {
      const cleaned = codeFromUrl.trim().toUpperCase();
      setCode(cleaned);
      setSubmittedCode(cleaned);
    }
  }, []);

  const {
    data: validated,
    error: validateError,
    isFetching: isValidating,
    refetch,
  } = useValidateGift(submittedCode);

  const redeemMutation = useRedeemGift();

  const handleValidate = () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    // If re-validating the same code, force a refetch; otherwise update the key.
    if (submittedCode === trimmed) {
      refetch();
    } else {
      setRedeemed(null);
      setSubmittedCode(trimmed);
    }
  };

  const handleRedeem = (asCredit = false) => {
    const codeToRedeem = submittedCode || validated?.code || code.trim();
    if (!codeToRedeem || !validated) return;

    redeemMutation.mutate(
      { code: codeToRedeem, asCredit },
      {
        onSuccess: (result) => {
          setRedeemed(result);
          toast({
            title: t("redeemPage.redeemed"),
            description: result.message,
          });
        },
        onError: (err: Error) => {
          toast({
            title: t("redeemPage.invalid"),
            description: err.message,
            variant: "destructive",
          });
        },
      },
    );
  };

  const isValue = validated?.type === "value";
  const isPackage = validated?.type === "package";
  const showInvalid = validateError && !isValidating;

  const invalidMessage = (() => {
    const msg = (validateError as Error)?.message?.toLowerCase() || "";
    if (msg.includes("expired")) return t("redeemPage.expired");
    if (msg.includes("used") || msg.includes("already")) return t("redeemPage.alreadyUsed");
    return t("redeemPage.invalid");
  })();

  return (
    <div className="min-h-screen pt-24 pb-24 bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 max-w-2xl">
        <h1 className="text-2xl font-bold flex items-center gap-2 mb-6">
          <Gift className="w-6 h-6 text-primary" />
          {t("redeemPage.title")}
        </h1>

        {/* Code input */}
        <Card className="rounded-xl border border-border/50 p-6 mb-6">
          <Label htmlFor="gift-code" className="mb-3 block">
            {t("redeemPage.enterCode")}
          </Label>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              id="gift-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t("redeemPage.codePlaceholder")}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleValidate();
              }}
              className="uppercase tracking-wider"
            />
            <Button
              onClick={handleValidate}
              disabled={!code.trim() || isValidating}
              className="sm:w-auto whitespace-nowrap"
            >
              {isValidating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("redeemPage.validating")}
                </>
              ) : (
                t("redeemPage.validate")
              )}
            </Button>
          </div>
        </Card>

        {/* Redeemed confirmation (value type) */}
        {redeemed && (
          <Card className="rounded-2xl p-6 mb-6 border-primary/40 bg-primary/5">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
              <p className="font-semibold text-lg">{t("redeemPage.redeemed")}</p>
            </div>
            {redeemed.value != null && (
              <p className="text-muted-foreground">
                {t("redeemPage.redeemedDesc", { value: Number(redeemed.value).toFixed(3) })}
              </p>
            )}
            {!redeemed.value && redeemed.message && (
              <p className="text-muted-foreground">{redeemed.message}</p>
            )}
          </Card>
        )}

        {/* Validation result preview */}
        {validated && !redeemed && (
          <Card className="rounded-2xl p-6 mb-6 border-primary/40">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              <p className="font-semibold">{t("redeemPage.valid")}</p>
            </div>

            {isValue && (
              <div className="bg-primary/5 rounded-xl p-4 mb-4 text-center">
                <p className="text-3xl font-bold text-primary">
                  {Number(validated.value).toFixed(3)}{" "}
                  <span className="text-xl font-medium opacity-90">
                    {t("services.currency")}
                  </span>
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("redeemPage.valueWillBeAdded", {
                    value: Number(validated.value).toFixed(3),
                  })}
                </p>
              </div>
            )}

            {isPackage && (
              <div className="bg-primary/5 rounded-xl p-4 mb-4">
                <p className="text-sm text-muted-foreground">{t("redeemPage.youGot")}</p>
                <p className="text-xl font-semibold text-primary">
                  {validated.serviceName}
                </p>
              </div>
            )}

            {validated.expiryDate && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-4">
                <Calendar className="h-3.5 w-3.5" />
                {t("redeemPage.expiryDate")}:{" "}
                {format(new Date(validated.expiryDate), "PPP", {
                  locale: isRtl ? ar : enUS,
                })}
              </p>
            )}

            {isValue && (
              <Button
                size="lg"
                className="w-full"
                onClick={() => handleRedeem()}
                disabled={redeemMutation.isPending}
              >
                {redeemMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("redeemPage.redeeming")}
                  </>
                ) : (
                  t("redeemPage.redeemNow")
                )}
              </Button>
            )}

            {isPackage && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground text-center">
                  {t("redeemPage.packageInstructions")}
                </p>
                <Button
                  size="lg"
                  className="w-full"
                  onClick={() => {
                    // Store gift info for the booking page
                    sessionStorage.setItem("giftBooking", JSON.stringify({
                      code: validated.code,
                      serviceName: validated.serviceName,
                      serviceId: validated.serviceId,
                      vehicleType: validated.vehicleType,
                    }));
                    // Navigate using wouter
                    navigate("/gift-booking");
                  }}
                >
                  <Calendar className="h-4 w-4 me-2" />
                  {t("redeemPage.bookService")}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => handleRedeem(true)}
                  disabled={redeemMutation.isPending}
                >
                  {redeemMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t("redeemPage.redeemAndCredit")
                  )}
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Invalid / error state */}
        {showInvalid && !redeemed && (
          <Card className="rounded-2xl p-6 mb-6 border-destructive/40">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <p className="font-semibold text-destructive">{invalidMessage}</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
